import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  FileText, 
  Plus, 
  Calculator,
  ArrowLeft,
  Download,
  Send,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter
} from "lucide-react";

const Invoices = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const [activeTab, setActiveTab] = useState("list"); // "list" or "create"
  const [invoiceData, setInvoiceData] = useState({
    // Client Information
    clientName: "",
    clientEmail: "",
    clientPhone: "",
    clientCompany: "",
    clientAddress: "",
    
    // Invoice Details
    invoiceNumber: `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-3)}`,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: "",
    quotationRef: "",
    
    // Services
    services: [
      { description: "", quantity: 1, rate: 0, amount: 0, customDescription: "" }
    ],
    
    // Additional Information
    notes: "",
    paymentTerms: "Payment due within 30 days\\nBank Transfer: Account Details\\nCheque payable to: ConsultPro"
  });

  // Sample invoice data
  const [invoices] = useState([
    {
      id: "INV-2024-001",
      client: "ABC Trading LLC",
      amount: 15750.00,
      status: "Paid",
      date: "2024-12-15",
      dueDate: "2025-01-15"
    },
    {
      id: "INV-2024-002", 
      client: "XYZ Consultancy",
      amount: 8400.00,
      status: "Pending",
      date: "2024-12-18",
      dueDate: "2025-01-18"
    },
    {
      id: "INV-2024-003",
      client: "Global Enterprises",
      amount: 22050.00,
      status: "Overdue",
      date: "2024-11-20",
      dueDate: "2024-12-20"
    },
    {
      id: "INV-2024-004",
      client: "Tech Solutions FZ",
      amount: 12600.00,
      status: "Draft",
      date: "2024-12-20",
      dueDate: "2025-01-20"
    }
  ]);

  const serviceOptions = [
    "Trade License Registration - Commercial",
    "Trade License Registration - Professional", 
    "Trade License Registration - Industrial",
    "Business Setup - LLC Formation",
    "Business Setup - Free Zone Company",
    "Bank Account Opening Assistance",
    "Visa Processing Services",
    "PRO Services",
    "Office Space Solutions",
    "Accounting & Bookkeeping",
    "Legal Consultation",
    "Document Attestation",
    "Custom Service"
  ];

  // Fetch client data if client_id is provided
  useEffect(() => {
    if (clientId) {
      fetchClientData(clientId);
    }
  }, [clientId]);

  const fetchClientData = async (id: string) => {
    try {
      const response = await fetch(`https://axlxwpuaqgbfkoampsai.supabase.co/functions/v1/handle_client_management_20251222_1800?client_id=${id}`, {
        headers: {
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF4bHh3cHVhcWdiZmtvYW1wc2FpIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0MDY3MDgsImV4cCI6MjA4MTk4MjcwOH0.kJ0f10vouM1ruwI2mwZ9tuAU_9df4l07Vzf1tE_tS6w',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const client = data.client;
        
        // Auto-populate client information
        setInvoiceData(prev => ({
          ...prev,
          clientName: client.full_name,
          clientEmail: client.email,
          clientPhone: client.phone || '',
          clientCompany: client.company_name || ''
        }));

        toast({
          title: "Client Data Loaded",
          description: `Invoice form populated with ${client.full_name}'s information`,
        });
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      toast({
        title: "Error",
        description: "Failed to load client data",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setInvoiceData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceChange = (index: number, field: string, value: string | number) => {
    const updatedServices = [...invoiceData.services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value
    };
    
    // Calculate amount for this service
    if (field === 'quantity' || field === 'rate') {
      updatedServices[index].amount = updatedServices[index].quantity * updatedServices[index].rate;
    }
    
    setInvoiceData(prev => ({
      ...prev,
      services: updatedServices
    }));
  };

  const addService = () => {
    setInvoiceData(prev => ({
      ...prev,
      services: [...prev.services, { description: "", quantity: 1, rate: 0, amount: 0, customDescription: "" }]
    }));
  };

  const removeService = (index: number) => {
    if (invoiceData.services.length > 1) {
      setInvoiceData(prev => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = () => {
    return invoiceData.services.reduce((total, service) => total + service.amount, 0);
  };

  const calculateVAT = () => {
    return calculateTotal() * 0.05; // 5% VAT
  };

  const calculateGrandTotal = () => {
    return calculateTotal() + calculateVAT();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invoiceData.clientName || !invoiceData.clientEmail) {
      toast({
        title: "Missing Information",
        description: "Please fill in client name and email.",
        variant: "destructive"
      });
      return;
    }

    if (invoiceData.services.some(service => !service.description || service.rate <= 0)) {
      toast({
        title: "Invalid Services",
        description: "Please ensure all services have descriptions and valid rates.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Invoice Generated Successfully!",
      description: `Invoice ${invoiceData.invoiceNumber} has been created and saved.`,
    });

    console.log("Invoice data:", invoiceData);
    setActiveTab("list");
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      "Paid": "default",
      "Pending": "secondary", 
      "Overdue": "destructive",
      "Draft": "outline"
    };
    return <Badge variant={variants[status as keyof typeof variants] as any}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-2xl font-bold text-primary">ConsultPro</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/crm" className="text-foreground hover:text-primary transition-colors">CRM</Link>
              <Button variant="outline" asChild>
                <Link to="/crm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to CRM
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="py-8 px-4 bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-4" variant="secondary">Invoice Management</Badge>
              <h1 className="text-4xl font-bold mb-2">Invoice System</h1>
              <p className="text-xl text-muted-foreground">
                Create, manage, and track professional invoices for your business consultancy services.
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant={activeTab === "list" ? "default" : "outline"}
                onClick={() => setActiveTab("list")}
              >
                <FileText className="h-4 w-4 mr-2" />
                Invoice List
              </Button>
              <Button 
                variant={activeTab === "create" ? "default" : "outline"}
                onClick={() => setActiveTab("create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          
          {activeTab === "list" && (
            <div className="space-y-6">
              {/* Filters and Search */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Invoice Management</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Search className="h-4 w-4 mr-2" />
                        Search
                      </Button>
                      <Button variant="outline" size="sm">
                        <Filter className="h-4 w-4 mr-2" />
                        Filter
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-4 mb-6">
                    <Card className="p-4 text-center">
                      <h3 className="text-2xl font-bold text-green-600">AED 15,750</h3>
                      <p className="text-sm text-muted-foreground">Paid This Month</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <h3 className="text-2xl font-bold text-orange-600">AED 8,400</h3>
                      <p className="text-sm text-muted-foreground">Pending</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <h3 className="text-2xl font-bold text-red-600">AED 22,050</h3>
                      <p className="text-sm text-muted-foreground">Overdue</p>
                    </Card>
                    <Card className="p-4 text-center">
                      <h3 className="text-2xl font-bold text-gray-600">AED 12,600</h3>
                      <p className="text-sm text-muted-foreground">Draft</p>
                    </Card>
                  </div>

                  {/* Invoice Table */}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.id}</TableCell>
                          <TableCell>{invoice.client}</TableCell>
                          <TableCell>AED {invoice.amount.toFixed(2)}</TableCell>
                          <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                          <TableCell>{invoice.date}</TableCell>
                          <TableCell>{invoice.dueDate}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm">
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button variant="outline" size="sm">
                                <Send className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === "create" && (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Left Column - Form */}
                <div className="lg:col-span-2 space-y-8">
                  
                  {/* Client Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Building2 className="h-5 w-5 mr-2" />
                        Client Information
                      </CardTitle>
                      <CardDescription>
                        Enter client details for the invoice
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="clientName">Client Name *</Label>
                        <Input
                          id="clientName"
                          value={invoiceData.clientName}
                          onChange={(e) => handleInputChange("clientName", e.target.value)}
                          placeholder="Enter client name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientCompany">Company Name</Label>
                        <Input
                          id="clientCompany"
                          value={invoiceData.clientCompany}
                          onChange={(e) => handleInputChange("clientCompany", e.target.value)}
                          placeholder="Enter company name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientEmail">Email Address *</Label>
                        <Input
                          id="clientEmail"
                          type="email"
                          value={invoiceData.clientEmail}
                          onChange={(e) => handleInputChange("clientEmail", e.target.value)}
                          placeholder="client@example.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientPhone">Phone Number</Label>
                        <Input
                          id="clientPhone"
                          value={invoiceData.clientPhone}
                          onChange={(e) => handleInputChange("clientPhone", e.target.value)}
                          placeholder="+971 XX XXX XXXX"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="clientAddress">Billing Address</Label>
                        <Textarea
                          id="clientAddress"
                          value={invoiceData.clientAddress}
                          onChange={(e) => handleInputChange("clientAddress", e.target.value)}
                          placeholder="Enter complete billing address"
                          rows={3}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Invoice Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <FileText className="h-5 w-5 mr-2" />
                        Invoice Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="invoiceNumber">Invoice Number</Label>
                        <Input
                          id="invoiceNumber"
                          value={invoiceData.invoiceNumber}
                          onChange={(e) => handleInputChange("invoiceNumber", e.target.value)}
                          readOnly
                          className="bg-gray-50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="quotationRef">Quotation Reference</Label>
                        <Input
                          id="quotationRef"
                          value={invoiceData.quotationRef}
                          onChange={(e) => handleInputChange("quotationRef", e.target.value)}
                          placeholder="Q-2024-XXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="invoiceDate">Invoice Date</Label>
                        <Input
                          id="invoiceDate"
                          type="date"
                          value={invoiceData.invoiceDate}
                          onChange={(e) => handleInputChange("invoiceDate", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dueDate">Due Date</Label>
                        <Input
                          id="dueDate"
                          type="date"
                          value={invoiceData.dueDate}
                          onChange={(e) => handleInputChange("dueDate", e.target.value)}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Services */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center">
                            <Calculator className="h-5 w-5 mr-2" />
                            Services & Pricing
                          </CardTitle>
                          <CardDescription>
                            Add services and their pricing details
                          </CardDescription>
                        </div>
                        <Button type="button" onClick={addService} size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add Service
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {invoiceData.services.map((service, index) => (
                        <div key={index} className="grid md:grid-cols-12 gap-4 items-end p-4 border rounded-lg">
                          <div className="md:col-span-5 space-y-2">
                            <Label>Service Description</Label>
                            <Select 
                              value={service.description} 
                              onValueChange={(value) => handleServiceChange(index, "description", value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select service" />
                              </SelectTrigger>
                              <SelectContent>
                                {serviceOptions.map((option) => (
                                  <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            {/* Manual input field for custom service */}
                            {service.description === "Custom Service" && (
                              <div className="mt-2">
                                <Label>Custom Service Description</Label>
                                <Input
                                  placeholder="Enter custom service description"
                                  value={service.customDescription || ""}
                                  onChange={(e) => handleServiceChange(index, "customDescription", e.target.value)}
                                />
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              min="1"
                              value={service.quantity}
                              onChange={(e) => handleServiceChange(index, "quantity", parseInt(e.target.value) || 1)}
                            />
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <Label>Rate (AED)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={service.rate}
                              onChange={(e) => handleServiceChange(index, "rate", parseFloat(e.target.value) || 0)}
                            />
                          </div>
                          <div className="md:col-span-2 space-y-2">
                            <Label>Amount (AED)</Label>
                            <Input
                              value={service.amount.toFixed(2)}
                              readOnly
                              className="bg-gray-50 font-semibold"
                            />
                          </div>
                          <div className="md:col-span-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeService(index)}
                              disabled={invoiceData.services.length === 1}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Additional Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Additional Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="notes">Notes</Label>
                        <Textarea
                          id="notes"
                          value={invoiceData.notes}
                          onChange={(e) => handleInputChange("notes", e.target.value)}
                          placeholder="Any additional notes or special instructions..."
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="paymentTerms">Payment Terms</Label>
                        <Textarea
                          id="paymentTerms"
                          value={invoiceData.paymentTerms}
                          onChange={(e) => handleInputChange("paymentTerms", e.target.value)}
                          rows={4}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Preview */}
                <div className="lg:col-span-1">
                  <Card className="sticky top-4">
                    <CardHeader>
                      <CardTitle>Invoice Preview</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Company Logo */}
                      <div className="text-center border-b pb-4">
                        <img 
                          src="./images/logo.jpg" 
                          alt="Company Logo" 
                          className="h-16 w-auto mx-auto mb-2"
                        />
                        <h3 className="font-bold text-lg">ConsultPro</h3>
                        <p className="text-sm text-muted-foreground">Business Consultancy Services</p>
                        <p className="text-xs text-muted-foreground mt-1">Dubai, UAE</p>
                      </div>

                      {/* Invoice Info */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">Invoice #:</span>
                          <span>{invoiceData.invoiceNumber}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Date:</span>
                          <span>{invoiceData.invoiceDate}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Due Date:</span>
                          <span>{invoiceData.dueDate || "N/A"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Client:</span>
                          <span>{invoiceData.clientName || "N/A"}</span>
                        </div>
                      </div>

                      <Separator />

                      {/* Totals */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Subtotal:</span>
                          <span>AED {calculateTotal().toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>VAT (5%):</span>
                          <span>AED {calculateVAT().toFixed(2)}</span>
                        </div>
                        <Separator />
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total:</span>
                          <span>AED {calculateGrandTotal().toFixed(2)}</span>
                        </div>
                      </div>

                      <Separator />

                      {/* Action Buttons */}
                      <div className="space-y-2">
                        <Button type="submit" className="w-full">
                          <FileText className="h-4 w-4 mr-2" />
                          Generate Invoice
                        </Button>
                        <Button type="button" variant="outline" className="w-full">
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </Button>
                        <Button type="button" variant="outline" className="w-full">
                          <Send className="h-4 w-4 mr-2" />
                          Send to Client
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Invoices;